import { APP } from 'src/Configs/App'
import { rtkApi } from 'src/Services/rtkApi'
import { Recipient } from 'src/Types/payments'
import { BankDetails } from 'src/Components/WisePayout/helpers'
import { useAppSelector } from 'src/Hooks/redux'
import { Roles } from 'src/Constants/enums'

import { getUserRole } from '../auth-process/credentialsSlice/selectors'

interface AddWiseRecipientBody {
  currency: string
  type: string
  details: BankDetails
}
const recipientApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getRecipientBankDetails: build.query<Recipient, void | null>({
      query: () => ({
        url: `${APP.TIKTOK_SERVER}/payout/transferwise/recipient`,
        method: 'GET',
      }),
      providesTags: ['Recipient'],
    }),
    getCreatorRecipient: build.query<Recipient, void | null>({
      query: () => ({
        url: `${APP.TIKTOK_SERVER}/creator/payouts/wise/recipients/current`,
        method: 'GET',
      }),
      providesTags: ['Recipient'],
    }),
    getCuratorRecipient: build.query<Recipient, void | null>({
      query: () => ({
        url: `${APP.TIKTOK_SERVER}/curator/payouts/wise/recipients/current`,
        method: 'GET',
      }),
      providesTags: ['Recipient'],
    }),

    addTransferwiseRecipient: build.mutation<Recipient, AddWiseRecipientBody>({
      query: (body) => ({
        url: `${APP.TIKTOK_SERVER}/payout/transferwise/recipient`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Recipient'],
    }),
    addCuratorRecipient: build.mutation<Recipient, AddWiseRecipientBody>({
      query: (body) => ({
        url: `${APP.TIKTOK_SERVER}/curator/payouts/wise/recipients/current`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Recipient'],
    }),
    addCreatorRecipient: build.mutation<Recipient, AddWiseRecipientBody>({
      query: (body) => ({
        url: `${APP.TIKTOK_SERVER}/creator/payouts/wise/recipients/current`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Recipient'],
    }),
  }),
})

export const {
  useGetRecipientBankDetailsQuery,
  useGetCreatorRecipientQuery,
  useGetCuratorRecipientQuery,
  useAddTransferwiseRecipientMutation,
  useAddCuratorRecipientMutation,
  useAddCreatorRecipientMutation,
} = recipientApi

export const useAddRecipientMutation = (): ((
  body: AddWiseRecipientBody,
) => Promise<Recipient>) => {
  const role = useAppSelector(getUserRole)
  const isNewWise = APP.NEW_WISE_PROCESS_ENABLED

  const [addTransferwiseRecipient] = useAddTransferwiseRecipientMutation()
  const [addCuratorRecipient] = useAddCuratorRecipientMutation()
  const [addCreatorRecipient] = useAddCreatorRecipientMutation()

  const wrapMutation =
    (mutation: typeof addTransferwiseRecipient) =>
    async (body: AddWiseRecipientBody) =>
      mutation(body).unwrap()

  if (!isNewWise) {
    return wrapMutation(addTransferwiseRecipient)
  }
  if (role === Roles.curator) {
    return wrapMutation(addCuratorRecipient)
  }
  return wrapMutation(addCreatorRecipient)
}

export const useRecipientQuery = (): Recipient | undefined => {
  const role = useAppSelector(getUserRole)
  const isNewWise = APP.NEW_WISE_PROCESS_ENABLED

  const { data: recipient } = useGetRecipientBankDetailsQuery(null, {
    skip: isNewWise,
  })
  const { data: curatorRecipient } = useGetCuratorRecipientQuery(null, {
    skip: !isNewWise || role !== Roles.curator,
  })
  const { data: creatorRecipient } = useGetCreatorRecipientQuery(null, {
    skip: !isNewWise || role !== Roles.influencer,
  })

  if (!isNewWise) {
    return recipient
  }
  if (role === Roles.curator) {
    return curatorRecipient
  }
  return creatorRecipient
}
